import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import {
  EmailConversationModel,
  parseEmailConvId,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { AttachmentType } from "@web-src/modules/chats/types";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { DisconnectedChatInput } from "../DisconnectedChatInput";
import { EmailForwardingRequiredPopup } from "../EmailForwardingRequiredPopup";
import { ReactComponent as ReplyIcon } from "./assets/reply.svg";

const EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB = 8;
const EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_BYTES =
  EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB * 1024 * 1024;

export const EmailChatInput: FC<{
  conversation: EmailConversationModel;
  onMessageSent?: () => void;
}> = ({ conversation, onMessageSent }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { setIsSendingMessage, incomingEmailMessages$ } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { emailAccount, isConnected } = useEmailAccount();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const [sendEmailMessage, { isLoading: isSendingEmailMessage }] =
    integrationsApi.useSendEmailMessageMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);
  const [isClientEmailRequiredPopupOpen, setIsClientEmailRequiredPopupOpen] =
    useState(false);

  const hasClientEmail =
    emailAccount?.type === "gmail" || !!emailAccount?.metadata?.client_email;

  const { email: recipientEmail } = useMemo(
    () => parseEmailConvId(conversation.conv_id),
    [conversation.conv_id]
  );

  useEffect(() => {
    setIsClientEmailRequiredPopupOpen(false);
  }, [conversation.conv_id]);

  const handleSendMessage: ChatInputParams["onSubmit"] = useCallback(
    async ({ body, bodyHtml, attachments }) => {
      if (!body.trim() && !attachments?.length) return;

      if (!hasClientEmail) {
        setIsClientEmailRequiredPopupOpen(true);
        return;
      }

      const totalAttachmentsSizeBytes =
        attachments?.reduce(
          (total, attachment) => total + attachment.file.size,
          0
        ) ?? 0;

      if (totalAttachmentsSizeBytes > EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_BYTES) {
        toast(
          t(
            {
              id: "client-support-page.email-attachments-total-size-exceeded",
              defaultMessage:
                "Total attachments size cannot exceed {maxSize}MB",
            },
            { maxSize: EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB }
          ),
          { variant: "error" }
        );
        return;
      }

      try {
        setIsSendingMessage(true);

        const sentMessage = await sendEmailMessage({
          entityId,
          data: {
            message_id: conversation.payload.messageId ?? "",
            name: conversation.conv_id,
            text: (bodyHtml ?? body).trim(),
            files: attachments?.map((attachment) => attachment.file),
          },
        }).unwrap();

        incomingEmailMessages$.next(sentMessage);

        setTimeout(() => {
          // clear() resets the body AND attachments; setValue("") leaves
          // attachments stuck in the composer
          chatInput$.current?.clear();
        }, 100);
        chatInput$.current?.focus();
        onMessageSent?.();
      } catch {
        // NOTE: pass
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      conversation,
      entityId,
      sendEmailMessage,
      onMessageSent,
      setIsSendingMessage,
      incomingEmailMessages$,
      hasClientEmail,
      toast,
      t,
    ]
  );

  if (!isConnected) {
    return (
      <DisconnectedChatInput
        title={t({
          id: "client-support-page.account-is-disconnected",
          defaultMessage: "Account is Disconnected",
        })}
        description={t(
          {
            id: "client-support-page.please-reconnect-to-continue-using-email-inbox",
            defaultMessage:
              "Please <highlightedText>reconnect</highlightedText> to continue using Email Inbox",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="font-medium text-primary">{text}</span>
            ),
          }
        )}
        buttonProps={{
          text: t({
            id: "client-support-page.open-settings",
            defaultMessage: "Open Settings",
          }),
          onClick: () => openManageAccountDrawer({ name: "emailSetup" }),
        }}
      />
    );
  }

  return (
    <>
      <div className="border-0 border-t border-solid border-dark-100">
        <div className="flex items-center gap-2 border-0 border-b border-solid border-dark-100 px-8 py-2.5">
          <ReplyIcon className="shrink-0" />
          <span className="truncate font-secondary text-xs text-[#828282]">
            {t(
              {
                id: "client-support-page.reply-to-email",
                defaultMessage: "To: {email}",
              },
              { email: recipientEmail }
            )}
          </span>
        </div>
        <div className="px-8 py-6">
          <ChatInput
            variant="inbox"
            id={conversation.conv_id}
            ref={chatInput$}
            onSubmit={handleSendMessage}
            isLoading={isSendingEmailMessage}
            placeholder={t({
              id: "client-support-page.enter-reply",
              defaultMessage: "Enter reply",
            })}
            toolbarActions={{
              emoji: {
                enabled: true,
              },
              attachments: {
                enabled: true,
                isMultiple: true,
                isCaptionHidden: true,
                accept: {
                  [AttachmentType.image]:
                    ".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg",
                  [AttachmentType.video]: ".mp4,.mov,.avi,.webm,.mkv,.3gp",
                  [AttachmentType.audio]: ".mp3,.m4a,.aac,.ogg,.wav,.amr",
                  [AttachmentType.file]:
                    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z",
                },
                maxSize: {
                  [AttachmentType.image]: EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB,
                  [AttachmentType.video]: EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB,
                  [AttachmentType.audio]: EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB,
                  [AttachmentType.file]: EMAIL_MAX_TOTAL_ATTACHMENTS_SIZE_MB,
                },
              },
              textFormatting: {
                enabled: true,
              },
            }}
          />
        </div>
      </div>
      <EmailForwardingRequiredPopup
        isOpen={isClientEmailRequiredPopupOpen}
        onRequestClose={() => setIsClientEmailRequiredPopupOpen(false)}
        onOpenSettings={() => {
          setIsClientEmailRequiredPopupOpen(false);
          openManageAccountDrawer({ name: "emailAccount" });
        }}
      />
    </>
  );
};
